import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Boxes,
  Receipt,
  UsersRound,
  Store,
  UserRound,
  Sparkles,
  Bell,
  BarChart3,
  type LucideIcon,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { getAppHost } from "../../lib/appHost";
import { NavUser } from "./nav-user";
import { OrgSwitcher } from "./org-switcher";

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
}

/** Client portal sidebar — mirrors the provider AppSidebar for a consistent shell.
 *  The Marketplace item appears only on the B2C app: a self-registered subscriber can
 *  keep browsing/subscribing (the platform API is org-tolerant, so no context switch). */
export function PortalSidebar(): JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isB2c = getAppHost() === "b2c";
  // Recommendations are a B2C-platform feature (needs a platform identity); pure
  // tenant clients (no platform_users row) don't see the item.
  // v2 is the shipped surface — v1 duplicates (Dashboard, Invoices, Recommendations)
  // are dropped from the sidebar.
  const nav: NavItem[] = [
    { label: "Dashboard", to: "/portal/dashboard", icon: LayoutDashboard },
    { label: "Recommended", to: "/portal/recommendations", icon: Sparkles },
    { label: "My Services", to: "/portal/subscriptions", icon: Boxes },
    ...(isB2c
      ? [{ label: "Marketplace", to: "/portal/marketplace", icon: Store }]
      : []),
    { label: "Invoices", to: "/portal/billing", icon: Receipt },
    { label: "Reports", to: "/portal/reports", icon: BarChart3 },
    { label: "Notifications", to: "/portal/notifications", icon: Bell },
    { label: "Team", to: "/portal/team", icon: UsersRound },
    { label: "Profile", to: "/portal/profile", icon: UserRound },
  ];

  // Longest matching `to` wins so nested routes highlight the right item.
  const activeTo = nav
    .filter((i) => pathname === i.to || pathname.startsWith(`${i.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0]?.to;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <OrgSwitcher variant="sidebar" />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Portal</SidebarGroupLabel>
          <SidebarMenu>
            {nav.map((item) => {
              const Icon = item.icon;
              return (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton
                    asChild
                    isActive={item.to === activeTo}
                    tooltip={item.label}
                  >
                    <Link to={item.to}>
                      <Icon />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <NavUser profileTo="/portal/profile" />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
